import React from 'react';
import { T } from '@tolgee/react';
import {
	TrackingDriverNoticeKey,
	TrackingTruck,
} from '../orderTracking.interface';
import { TRACKING_DRIVER_NOTICES } from '../orderTracking.constants';
import {
	clampProgress,
	formatArrival,
	formatTime,
	isTruckComplete,
	reachedStepsCount,
} from '../orderTracking.utils';
import CarrierCompanyTruck from '../../../../assets/CarrierCompanyTruck';
import { cn } from '../../../../lib/utils';

interface EtaCardProps {
	truck: TrackingTruck;
	distanceKm: number;
	updatedAt: string;
	notice?: TrackingDriverNoticeKey;
	className?: string;
	mobileClassName?: string;
}

const EtaCard: React.FC<EtaCardProps> = ({
	truck,
	distanceKm: orderDistanceKm,
	updatedAt,
	notice,
	className,
	mobileClassName,
}) => {
	const distanceKm = truck.routeDistanceKm ?? orderDistanceKm;
	const complete = isTruckComplete(truck);
	const started = reachedStepsCount(truck) > 0;

	const liveToDelivery =
		truck.liveEtaTo === 'delivery' && truck.liveEtaKm !== undefined;
	const progress =
		liveToDelivery && distanceKm > 0
			? clampProgress(1 - truck.liveEtaKm! / distanceKm)
			: clampProgress(truck.routeProgress);
	const percent = Math.round(progress * 100);
	const remainingKm = Math.round(
		truck.liveEtaKm ?? distanceKm * (1 - progress),
	);

	const onLiveLeg = !complete && !notice && truck.liveEtaTo !== undefined;
	const toPickup = onLiveLeg && truck.liveEtaTo === 'pickup';
	const arrivedAt = truck.steps
		.filter((step) => step.completed && step.completedAt)
		.map((step) => step.completedAt as string)
		.pop();

	const liveArrival = onLiveLeg ? formatArrival(truck.liveEta) : undefined;
	const calculating = onLiveLeg && !liveArrival;
	const etaLabel = onLiveLeg
		? liveArrival || <T keyName="orderTracking.calculatingEta">Calculating…</T>
		: truck.eta
		? `${formatTime(truck.eta.from)}${
				truck.eta.to ? ` – ${formatTime(truck.eta.to)}` : ''
		  }`
		: '—';
	const timeLabel = complete ? formatTime(arrivedAt) || '—' : etaLabel;

	const title = notice ? (
		<T keyName="orderTracking.scheduledArrival">Scheduled arrival</T>
	) : complete ? (
		<T keyName="orderTracking.arrived">Arrived</T>
	) : toPickup ? (
		<T keyName="orderTracking.arrivalAtPickup">Arrival at pick-up</T>
	) : started ? (
		<T keyName="orderTracking.estimatedArrival">Estimated arrival</T>
	) : (
		<T keyName="orderTracking.scheduledArrival">Scheduled arrival</T>
	);

	const distanceLine = notice ? (
		<T keyName={TRACKING_DRIVER_NOTICES[notice].labelKey}>
			{TRACKING_DRIVER_NOTICES[notice].defaultLabel}
		</T>
	) : toPickup ? (
		<T keyName="orderTracking.kmToPickup" params={{ km: remainingKm }}>
			{'{km} km to pick-up'}
		</T>
	) : distanceKm > 0 && (complete || started) ? (
		complete ? (
			<T
				keyName="orderTracking.kmDriven"
				params={{ km: Math.round(distanceKm) }}
			>
				{'{km} km driven · 100% of route complete'}
			</T>
		) : (
			<T
				keyName="orderTracking.kmRemaining"
				params={{ km: remainingKm, pct: percent }}
			>
				{'{km} km remaining · {pct}% of route complete'}
			</T>
		)
	) : (
		<T keyName="orderTracking.tripNotStarted">Trip not started yet</T>
	);

	return (
		<>
			<div
				className={cn(
					'sm:hidden absolute left-4 right-4 top-[74px] z-[600] flex items-center gap-3 bg-white border border-solid border-track-line rounded-[10px] px-3 py-2 shadow-[0_2px_12px_rgba(31,31,39,0.08)]',
					mobileClassName,
				)}
			>
				<span className="w-9 h-9 rounded-lg bg-track-indigo-soft flex items-center justify-center flex-none">
					<CarrierCompanyTruck
						color="#484880"
						width={18}
						height={18}
						strokeWidth="1.5"
					/>
				</span>
				<div className="min-w-0 flex-1">
					<div className="flex items-baseline gap-2">
						<span className="text-[11px] text-track-muted whitespace-nowrap">
							{title}
						</span>
						<span
							className={cn(
								'text-base whitespace-nowrap',
								calculating
									? 'font-medium text-track-muted'
									: 'font-bold text-track-ink',
							)}
						>
							{timeLabel}
						</span>
					</div>
					<div
						className={cn(
							'text-[11.5px] text-track-slate',
							notice ? 'leading-[1.4]' : 'truncate',
						)}
					>
						{distanceLine}
					</div>
				</div>
			</div>

			<div
				className={cn(
					'hidden sm:block absolute left-5 top-[78px] lg:top-[100px] z-[600] w-[357px] max-w-[calc(100%-40px)] bg-white border border-solid border-track-line rounded-[10px] px-5 py-[18px] shadow-[0_2px_12px_rgba(31,31,39,0.08)]',
					className,
				)}
			>
				<div className="flex items-center justify-between gap-2 text-[12.5px]">
					<span className="text-track-muted">{title}</span>
					<span className="flex items-center gap-[6px] text-track-slate whitespace-nowrap">
						<span className="w-[7px] h-[7px] rounded-full bg-track-teal flex-none" />
						<T keyName="orderTracking.updated">Updated</T>
						<span className="text-track-muted">·</span>
						{formatTime(truck.positionAt ?? updatedAt)}
					</span>
				</div>
				<div
					className={cn(
						'text-[26px] tracking-[-0.01em] leading-[1.25] mt-[2px] mb-[10px]',
						calculating
							? 'font-medium text-track-muted'
							: 'font-bold text-track-ink',
					)}
				>
					{timeLabel}
				</div>
				<div className="flex items-center gap-2 text-[13px] text-track-body">
					<span className="flex flex-none">
						<CarrierCompanyTruck
							color="#484880"
							width={16}
							height={16}
							strokeWidth="1.5"
						/>
					</span>
					<span>{distanceLine}</span>
				</div>
			</div>
		</>
	);
};

export default EtaCard;
